import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { Controller, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid-pro';

import PageHeader from '~/components/Common/Elements/PageHeader';
import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER } from '~/consts/roles';

import {
  useGetEmailDeliveryFailures,
  useResendEmailDeliveryFailure,
} from '~/hooks/apis/sailthru/useEmailDeliveryFailures';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import {
  EmailDeliveryFailureBrand,
  EmailDeliveryFailureNotificationType,
  EmailDeliveryFailureRecord,
  EmailDeliveryFailuresLookbackDays,
} from '~/services/SailthruService';

import EmailDeliveryFailuresTable from './EmailDeliveryFailuresTable';
import {
  DEFAULT_EMAIL_DELIVERY_FAILURES_LOOKBACK_DAYS,
  EMAIL_DELIVERY_FAILURES_LOOKBACK_DAY_OPTIONS,
  EMAIL_DELIVERY_FAILURES_PAGE_SIZE,
  EMAIL_DELIVERY_FAILURE_NOTIFICATION_TYPES,
  EMAIL_DELIVERY_FAILURE_NOTIFICATION_TYPE_GROUP_LABELS,
} from './constants';

const DEFAULT_PAGE = 1;
const EMAIL_ADDRESS_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type EmailDeliveryFailureFiltersForm = {
  email: string;
  lookbackDays: EmailDeliveryFailuresLookbackDays;
  notificationTypes: Array<EmailDeliveryFailureNotificationType>;
};

type ParsedQuery = EmailDeliveryFailureFiltersForm & {
  page: number;
};

const DEFAULT_FILTER_VALUES: EmailDeliveryFailureFiltersForm = {
  email: '',
  lookbackDays: DEFAULT_EMAIL_DELIVERY_FAILURES_LOOKBACK_DAYS,
  notificationTypes: [],
};

function isEmailDeliveryFailureNotificationType(value: string): value is EmailDeliveryFailureNotificationType {
  return EMAIL_DELIVERY_FAILURE_NOTIFICATION_TYPES.includes(value as EmailDeliveryFailureNotificationType);
}

function parsePage(value: string | null): number {
  const parsedPage = Number(value);
  return Number.isInteger(parsedPage) && parsedPage >= DEFAULT_PAGE ? parsedPage : DEFAULT_PAGE;
}

function parseLookbackDays(value: string | null): EmailDeliveryFailuresLookbackDays {
  const parsedLookbackDays = Number(value);

  return EMAIL_DELIVERY_FAILURES_LOOKBACK_DAY_OPTIONS.includes(parsedLookbackDays as EmailDeliveryFailuresLookbackDays)
    ? (parsedLookbackDays as EmailDeliveryFailuresLookbackDays)
    : DEFAULT_EMAIL_DELIVERY_FAILURES_LOOKBACK_DAYS;
}

function parseNotificationTypes(
  value: string | null,
  brand?: EmailDeliveryFailureBrand,
): Array<EmailDeliveryFailureNotificationType> {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => Boolean(item) && (brand || isEmailDeliveryFailureNotificationType(item)));
}

function parseQuery(search: string, brand?: EmailDeliveryFailureBrand): ParsedQuery {
  const params = new URLSearchParams(search);

  return {
    page: parsePage(params.get('page')),
    email: params.get('email')?.trim() ?? '',
    lookbackDays: parseLookbackDays(params.get('lookback_days')),
    notificationTypes: parseNotificationTypes(params.get('notification_types'), brand),
  };
}

function buildSearch(filters: EmailDeliveryFailureFiltersForm, page = DEFAULT_PAGE): string {
  const params = new URLSearchParams();
  const email = filters.email.trim();

  params.set('page', String(page));

  if (email) {
    params.set('email', email.toLowerCase());
  }

  if (filters.lookbackDays !== DEFAULT_EMAIL_DELIVERY_FAILURES_LOOKBACK_DAYS) {
    params.set('lookback_days', String(filters.lookbackDays));
  }

  if (filters.notificationTypes.length > 0) {
    params.set('notification_types', filters.notificationTypes.join(','));
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

interface Props {
  brand?: EmailDeliveryFailureBrand;
  embedded?: boolean;
}

export default function EmailDeliveryFailuresPage({ brand, embedded = false }: Props = {}) {
  const location = useLocation();
  const { push } = useAppNavigation();
  const { user } = useCurrentUser();
  const [selectedNotificationIds, setSelectedNotificationIds] = useState<GridRowSelectionModel>([]);
  const parsedQuery = useMemo(() => parseQuery(location.search, brand), [location.search, brand]);

  const form = useForm<EmailDeliveryFailureFiltersForm>({
    defaultValues: parsedQuery,
    mode: 'onChange',
  });

  const formValuesFromQuery = useMemo<EmailDeliveryFailureFiltersForm>(
    () => ({
      email: parsedQuery.email,
      lookbackDays: parsedQuery.lookbackDays,
      notificationTypes: parsedQuery.notificationTypes,
    }),
    [parsedQuery.email, parsedQuery.lookbackDays, parsedQuery.notificationTypes],
  );

  useEffect(() => {
    form.reset(formValuesFromQuery);
  }, [form, formValuesFromQuery]);

  const emailFilter = EMAIL_ADDRESS_REGEX.test(parsedQuery.email) ? parsedQuery.email.toLowerCase() : undefined;
  const queryFilters = useMemo(
    () => ({
      brand,
      page: parsedQuery.page,
      email: emailFilter,
      lookbackDays: parsedQuery.lookbackDays,
      notificationTypes: parsedQuery.notificationTypes.length ? parsedQuery.notificationTypes : undefined,
    }),
    [brand, emailFilter, parsedQuery.lookbackDays, parsedQuery.notificationTypes, parsedQuery.page],
  );

  const { data, isLoading, error } = useGetEmailDeliveryFailures(queryFilters);
  const resendEmailMutation = useResendEmailDeliveryFailure();

  // Sailthru returns the full configured type list, independent of rows and filters.
  // LEBT requires the companion service deployment before this metadata is available.
  const notificationTypeOptions = brand
    ? (data?.result.notificationTypes ?? [])
    : EMAIL_DELIVERY_FAILURE_NOTIFICATION_TYPES;
  const rows = data?.result.data ?? [];
  const total = data?.result.pagination.total ?? 0;
  const paginationModel = useMemo<GridPaginationModel>(
    () => ({
      page: parsedQuery.page - 1,
      pageSize: EMAIL_DELIVERY_FAILURES_PAGE_SIZE,
    }),
    [parsedQuery.page],
  );

  const hasActiveFilters =
    Boolean(parsedQuery.email) ||
    parsedQuery.lookbackDays !== DEFAULT_EMAIL_DELIVERY_FAILURES_LOOKBACK_DAYS ||
    parsedQuery.notificationTypes.length > 0;
  const resendingNotificationId = resendEmailMutation.isPending ? resendEmailMutation.variables?.id : undefined;

  const navigateWithFilters = useCallback(
    (filters: EmailDeliveryFailureFiltersForm, page = DEFAULT_PAGE) => {
      push({
        pathname: location.pathname,
        search: buildSearch(filters, page),
      });
    },
    [location.pathname, push],
  );

  const handleSubmit = useCallback(
    (filters: EmailDeliveryFailureFiltersForm) => {
      navigateWithFilters(filters, DEFAULT_PAGE);
    },
    [navigateWithFilters],
  );

  const handleClearFilters = useCallback(() => {
    form.reset(DEFAULT_FILTER_VALUES);
    navigateWithFilters(DEFAULT_FILTER_VALUES, DEFAULT_PAGE);
  }, [form, navigateWithFilters]);

  const handlePaginationModelChange = useCallback(
    (nextPaginationModel: GridPaginationModel) => {
      navigateWithFilters(formValuesFromQuery, nextPaginationModel.page + 1);
    },
    [formValuesFromQuery, navigateWithFilters],
  );

  const handleResend = useCallback(
    (row: EmailDeliveryFailureRecord) => {
      resendEmailMutation.mutate({ id: row.notificationId });
    },
    [resendEmailMutation],
  );

  const handleResendSelected = useCallback(async () => {
    // TODO: Use a batched resend endpoint if this becomes a performance concern. See OPEX-5325: https://aussiecommerce.atlassian.net/browse/OPEX-5325
    for (const notificationId of selectedNotificationIds) {
      try {
        await resendEmailMutation.mutateAsync({ id: String(notificationId) });
      } catch {
        // Continue resending the remaining selected notifications.
      }
    }
  }, [resendEmailMutation, selectedNotificationIds]);

  return (
    <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
      <Helmet>
        <title>{brand ? 'Email | LEBT Email Delivery Failures' : 'System | Email Delivery Failures'}</title>
      </Helmet>

      <Box
        sx={{
          flex: 1,
          height: embedded ? 'auto' : 'calc(100vh - 64px)',
          minHeight: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          px: embedded ? 0 : 2,
          pt: 1,
          pb: 2,
          boxSizing: 'border-box',
          overflow: embedded ? 'visible' : 'hidden',
        }}
      >
        <Box sx={{ flexShrink: 0, '& h1': { mb: 0.5 } }}>
          {!embedded && <PageHeader title="Email Delivery Failures" />}
          <Typography color="text.secondary" variant="body2">
            {brand
              ? 'Monitor delivery failures for tracked LEBT emails, resend or forward emails, and record how issues were handled.'
              : 'Monitor failed vendor notifications, take action to resolve issues, and prevent the same emails from failing again.'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ flexShrink: 0, mt: 2 }}>
            Failed to load email delivery failures. Please try again.
          </Alert>
        )}

        <Box component="form" onSubmit={form.handleSubmit(handleSubmit)} noValidate sx={{ flexShrink: 0, mt: 2 }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'flex-start' }}>
            <Controller
              name="email"
              control={form.control}
              rules={{
                pattern: {
                  value: EMAIL_ADDRESS_REGEX,
                  message: 'Enter a valid email address',
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="email"
                  label="Email address"
                  size="small"
                  sx={{ minWidth: 300 }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Box>
              <Controller
                name="lookbackDays"
                control={form.control}
                render={({ field }) => (
                  <ToggleButtonGroup
                    color="primary"
                    exclusive
                    size="small"
                    value={field.value}
                    onChange={(_, nextValue: EmailDeliveryFailuresLookbackDays | null) => {
                      if (nextValue) {
                        field.onChange(nextValue);
                      }
                    }}
                  >
                    {EMAIL_DELIVERY_FAILURES_LOOKBACK_DAY_OPTIONS.map((lookbackDays) => (
                      <ToggleButton key={lookbackDays} value={lookbackDays}>
                        {lookbackDays} days
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Defaults to the last 7 days
              </Typography>
            </Box>

            <Controller
              name="notificationTypes"
              control={form.control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={notificationTypeOptions}
                  loading={isLoading}
                  disabled={Boolean(brand) && !data?.result.notificationTypes}
                  value={field.value}
                  groupBy={
                    brand ? undefined : (option) => EMAIL_DELIVERY_FAILURE_NOTIFICATION_TYPE_GROUP_LABELS[option]
                  }
                  onChange={(_, nextValue) => field.onChange(nextValue)}
                  renderGroup={(params) => (
                    <Box component="li" key={params.key}>
                      <Typography color="text.secondary" sx={{ px: 2, py: 1 }} variant="subtitle2">
                        {params.group}
                      </Typography>
                      <Box component="ul" sx={{ p: 0 }}>
                        {params.children}
                      </Box>
                      <Divider />
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Notification types"
                      placeholder="Select notification types"
                      size="small"
                    />
                  )}
                  sx={{ minWidth: 360, flex: 1 }}
                />
              )}
            />

            <Button type="submit" variant="contained" disabled={!form.formState.isValid || !form.formState.isDirty}>
              Search
            </Button>
            <Button variant="text" onClick={handleClearFilters} disabled={!hasActiveFilters && !form.formState.isDirty}>
              Clear filters
            </Button>
          </Stack>
        </Box>

        <Box
          sx={{
            flex: embedded ? undefined : 1,
            minHeight: 0,
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: embedded ? 'visible' : 'hidden',
          }}
        >
          <EmailDeliveryFailuresTable
            autoHeight={embedded}
            rows={rows}
            total={total}
            isLoading={isLoading}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            onResend={handleResend}
            onResendSelected={handleResendSelected}
            resendingNotificationId={resendingNotificationId}
            currentUserEmail={user?.email}
            selectedNotificationIds={selectedNotificationIds}
            onSelectionChange={setSelectedNotificationIds}
            isResending={resendEmailMutation.isPending}
            lookbackDays={parsedQuery.lookbackDays}
          />
        </Box>
      </Box>
    </PermissionedComponent>
  );
}
